import axios from "axios";
import {
  and,
  collection,
  doc,
  documentId,
  limit,
  orderBy,
  query,
  setDoc,
  where,
  type QueryFilterConstraint,
  type QueryNonFilterConstraint,
  type OrderByDirection,
  type WhereFilterOp,
} from "firebase/firestore";
import type { IContentsRepository } from "~/interfaces/IContentsRepository";
import { UserContentsInfo, UserContentsInfoConverter } from "~/entities/UserContentsInfo";
import { PlanContentsInfo, PlanContentsInfoConverter } from "~/entities/PlanContentsInfo";
import { ContentsResponse } from "~/entities/ContentsResponse";
import { FIRESTORE_WHERE_IN_LIMIT } from "~/consts/Firebase";
import { useLoginStore } from "~/stores/LoginStore";

type CheckUnreadResult = {
  result: boolean;
  latestCheckInfo: {
    checkedUntilAt?: Date;
    lastUnreadBoundaryAt?: Date;
  };
};

/**
 * コンテンツが閲覧可能かどうかを判定する
 *
 * 判定条件:
 * 1. プラン制限チェック（limitedPlanPriceIds）
 *    - 設定がある場合、ユーザーのプランIDが含まれていない場合は閲覧不可
 * 2. 閲覧制限日数チェック（viewingRestrictionDays）
 *    - 未設定または0の場合は閲覧可能
 *    - 設定がある場合、ユーザーの契約日数が制限日数以上の場合のみ閲覧可能
 *
 * @param content チェック対象のコンテンツ情報
 * @param userPlanPriceId ユーザーのプランPriceID（プラン制限チェックに使用）
 * @param userContractDays ユーザーの契約日数（閲覧制限日数チェックに使用）
 * @returns 閲覧可能な場合はtrue、不可の場合はfalse
 */
function isContentsViewable(content: PlanContentsInfo, userPlanPriceId?: number, userContractDays?: number): boolean {
  // 1. プラン制限チェック
  // limitedPlanPriceIdsが設定されている場合、ユーザーのプランIDが含まれているかチェック
  if (content.limitedPlanPriceIds && content.limitedPlanPriceIds.length > 0) {
    // ユーザーのプランIDが含まれていない場合は閲覧不可
    if (!userPlanPriceId || !content.limitedPlanPriceIds.includes(userPlanPriceId)) {
      return false;
    }
  }

  // 2. 閲覧制限日数チェック
  // viewingRestrictionDaysが未設定または0の場合は閲覧可能（制限なし）
  if (!content.viewingRestrictionDays || content.viewingRestrictionDays === 0) {
    return true;
  }

  // userContractDaysが未定義の場合は閲覧不可
  // （閲覧制限日数が設定されているが、ユーザーの契約日数が不明なため）
  if (userContractDays === undefined) {
    return false;
  }

  // 契約日数が閲覧制限日数以上の場合は閲覧可能
  return userContractDays >= content.viewingRestrictionDays;
}

export class ContentsRepository implements IContentsRepository {
  /**
   * コンテンツの公開非公開情報を変更する
   */
  async putPublish(contentsId: string, publishStatus: string): Promise<void> {
    const res = await PutRequest<{ statusCode: number }>(
      `/contents_manage/publish`,
      JSON.stringify({
        contents_list: [
          {
            contents_id: contentsId,
            publish_status: publishStatus,
          },
        ],
      }),
    );
    if (!res || res.statusCode !== 200) {
      throw new Error("Failed to put publish");
    }
  }

  /**
   * コンテンツ閲覧フラグを立てる
   */
  async postBrowse(contentsId: string, userId: string): Promise<void> {
    // firestore の既読フラグを更新
    const userInfo = await this.getUserContentsInfo(userId, contentsId);
    if (!userInfo.read) {
      userInfo.read = true;
      userInfo.contentsId = Number(contentsId);
      userInfo.readAt = new Date();
      await this.putUserContentsInfo(userId, contentsId, userInfo);
    }

    // コンテンツ閲覧ロギング用API
    const apiBaseUrl = useRuntimeConfig().public.contentsBrowseLoggingApiBaseUrl as string;
    if (apiBaseUrl.length > 0) {
      await axios.post(`${apiBaseUrl}/contents`, {
        user_id: userId,
        contents_id: Number(contentsId),
      });
    }
  }

  /**
   * コンテンツ視聴完了
   */
  async postComplete(contentsId: string): Promise<void> {
    const res = await PostRequest<{ statusCode: number }>(`/contents/complete/${contentsId}`);
    if (!res || res.statusCode !== 200) {
      throw new Error("Failed to post complete");
    }
  }

  /**
   * いいねする
   */
  async postGood(contentsId: string): Promise<void> {
    const res = await PostRequest<{ statusCode: number }>(`/contents/good/${contentsId}`);
    if (!res || res.statusCode !== 200) {
      throw new Error("Failed to post good");
    }
  }

  /**
   * いいね解除
   */
  async deleteGood(contentsId: string): Promise<void> {
    const res = await DeleteRequest<{ statusCode: number }>(`/contents/good/${contentsId}`);
    if (!res || res.statusCode !== 200) {
      throw new Error("Failed to delete good");
    }
  }

  /**
   * ブックマークする
   */
  async postBookmark(contentsId: string): Promise<void> {
    const res = await PostRequest<{ statusCode: number }>(`/contents/bookmark/${contentsId}`);
    if (!res || res.statusCode !== 200) {
      throw new Error("Failed to post bookmark");
    }
  }

  /**
   * ブックマーク解除
   */
  async deleteBookmark(contentsId: string): Promise<void> {
    const res = await DeleteRequest<{ statusCode: number }>(`/contents/bookmark/${contentsId}`);
    if (!res || res.statusCode !== 200) {
      throw new Error("Failed to delete bookmark");
    }
  }

  /**
   * ユーザーごとのコンテンツリストを取得
   */
  async getUserContentsList(userId: string, contentsIds: string[]): Promise<UserContentsInfo[]> {
    if (contentsIds.length > FIRESTORE_WHERE_IN_LIMIT) {
      throw new Error(
        "getUserContentsList: contentsIds must have 30 or less items due to limit of where in statement of firestore query",
      );
    }
    const querySnapshot = await getFirestoreDocs(
      query(this.userContentsInfoCollection(userId), where(documentId(), "in", contentsIds)),
    );
    if (querySnapshot.empty) {
      return []; // Or however you wish to handle an empty result
    }
    return querySnapshot.docs.map(
      doc =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as UserContentsInfo,
    );
  }

  /**
   * ユーザーごとのコンテンツリストを一括取得
   * FIRESTORE_WHERE_IN_LIMIT以上のデータも取得可能
   */
  async getUserContentsListAll(userId: string, contentsIds: string[]): Promise<UserContentsInfo[]> {
    const allUserContentsInfo: UserContentsInfo[] = [];

    // firestoreのwhere in の制限により、FIRESTORE_WHERE_IN_LIMIT件ずつ取得する
    for (let i = 0; i < contentsIds.length; i += FIRESTORE_WHERE_IN_LIMIT) {
      const chunk = contentsIds.slice(i, i + FIRESTORE_WHERE_IN_LIMIT);
      const result = await this.getUserContentsList(userId, chunk);
      allUserContentsInfo.push(...result);
    }

    return allUserContentsInfo;
  }

  /**
   * ユーザーごとのコンテンツ情報を取得
   */
  async getUserContentsInfo(userId: string, contentsId: string): Promise<UserContentsInfo> {
    const snap = await getFirestoreDoc(this.userContentsInfoDoc(userId, contentsId));
    if (!snap.exists()) {
      return UserContentsInfo.empty();
    }
    return snap.data();
  }

  /**
   * ユーザーごとのコンテンツ情報を更新
   */
  async putUserContentsInfo(userId: string, contentsId: string, info: UserContentsInfo): Promise<void> {
    await setDoc(this.userContentsInfoDoc(userId, contentsId), info);
  }

  // ユーザーごとのコンテンツ情報リストを取得
  // /users/{userId}/contents
  userContentsInfoCollection(userId: string) {
    const { $firestore } = useNuxtApp();
    return collection($firestore, "users", userId, "contents").withConverter(UserContentsInfoConverter);
  }

  // ユーザーごとのコンテンツ情報を取得
  // /users/{userId}/contents/{contentsId}
  userContentsInfoDoc(userId: string, contentsId: string) {
    const { $firestore } = useNuxtApp();
    return doc($firestore, "users", userId, "contents", contentsId).withConverter(UserContentsInfoConverter);
  }

  /**
   * コンテンツ情報をAPIから取得
   * @param page ページ番号
   * @param pageSize 1ページあたりの件数
   * @param options contents_types: コンテンツタイプリスト([movie/live_movie/archive_movie/archive_movie_progress/document/schedule])
   * @param options exclusion_contents_id: 除外するコンテンツID
   * @param options is_contracted: 契約済みのコンテンツのみ取得するか
   * @param options is_free: 無料のコンテンツのみ取得するか
   * @param options owner_user_id: コンテンツの所有者のユーザーID
   * @param options plan_id: プランID
   * @param options search_word: 検索キーワード
   * @param options sort: ソート順[desc/asc]（nullの場合desc)
   * @param options tag: タグ
   * @param options user_role: ユーザーのロール
   * @param options is_bookmark: ブックマークしたコンテンツのみ取得するか
   * @param options is_ignore_featured: 固定コンテンツ無視フラグ
   */
  async getList<T>(page: number, pageSize: number, options?: Record<string, any>): Promise<T> {
    const paramsObj: { [name: string]: string } = {
      page: page.toString(),
      page_size: pageSize.toString(),
      ...filterEmptyObject(options || {}),
    };
    const searchParams = new URLSearchParams(paramsObj);
    const result = await GetRequest<T>(`/contents/list?` + searchParams.toString());

    // limitedPlanPriceIdsを付与する処理
    if (options?.plan_id) {
      interface ContentsList {
        contents: { id: number; limitedPlanPriceIds?: number[] }[];
      }
      const contentsList = result as ContentsList;

      if (contentsList.contents && contentsList.contents.length > 0) {
        const contentsIds = contentsList.contents.map(content => content.id.toString());
        const planContentsInfo = await this.getPlanContentsListByIdsAll(options.plan_id, contentsIds);

        // Mapを使用して検索性能を向上
        const planContentsMap = new Map(planContentsInfo.map(pc => [pc.contentsId.toString(), pc]));

        // 負荷の問題で、APIのレスポンスにlimitedPlanPriceIdsが含まれていないため、Firestoreから取得して付与
        contentsList.contents.forEach(content => {
          const planContent = planContentsMap.get(content.id.toString());
          content.limitedPlanPriceIds = planContent?.limitedPlanPriceIds ?? [];
        });
      }
    }

    return result;
  }

  /**
   * コンテンツ詳細をAPIから取得
   * @param contentsId コンテンツID
   * @returns コンテンツ情報
   */
  async getDetail<T>(contentsId: string): Promise<T> {
    // エラー時には共通ダイアログを表示せず、エラーをthrowする
    const response = await GetRequest<ContentsResponse<T>>(`/contents/${contentsId}`, false, true);
    return response?.contents;
  }

  // プランごとのコンテンツリストを取得
  // /contents/{planId}/contents
  planContentsInfoCollection(planId: string) {
    const { $firestore } = useNuxtApp();
    return collection($firestore, "contents", planId, "contents").withConverter(PlanContentsInfoConverter);
  }

  /**
   * プランごとのコンテンツリストをFirestoreから取得
   * @param planId プランID
   * @param contentsType コンテンツタイプ
   * @param filters フィルタ
   * @param nonFilters 非フィルタ
   * @returns PlanContentsInfoの配列
   */
  async getPlanContentsList(
    planId: string,
    contentsType: string,
    filters: QueryFilterConstraint[] = [],
    nonFilters: QueryNonFilterConstraint[] = [],
  ): Promise<PlanContentsInfo[]> {
    const compositeFilter = and(where("contents_type", "==", contentsType), ...filters);
    const querySnapshot = await getFirestoreDocs(
      query(this.planContentsInfoCollection(planId), compositeFilter, ...nonFilters),
    );
    // PlanContentsInfoConverterにより既にPlanContentsInfoオブジェクトが返される
    return querySnapshot.docs.map(doc => doc.data());
  }

  /**
   * プランごとのコンテンツリストをIDで取得
   * @param planId プランID
   * @param contentsIds コンテンツIDのリスト
   * @returns PlanContentsInfoの配列
   */
  async getPlanContentsListByIds(planId: string, contentsIds: string[]): Promise<PlanContentsInfo[]> {
    if (contentsIds.length > FIRESTORE_WHERE_IN_LIMIT) {
      throw new Error(
        "getPlanContentsListByIds: contentsIds must have 30 or less items due to limit of where in statement of firestore query",
      );
    }
    const querySnapshot = await getFirestoreDocs(
      query(
        this.planContentsInfoCollection(planId),
        where(
          "contents_id",
          "in",
          contentsIds.map(id => Number(id)),
        ),
      ),
    );
    if (querySnapshot.empty) {
      return [];
    }
    return querySnapshot.docs.map(doc => doc.data());
  }

  /**
   * プランごとのコンテンツリストをIDで一括取得
   * FIRESTORE_WHERE_IN_LIMIT以上のデータも取得可能
   * @param planId プランID
   * @param contentsIds コンテンツIDのリスト
   * @returns PlanContentsInfoの配列
   */
  async getPlanContentsListByIdsAll(planId: string, contentsIds: string[]): Promise<PlanContentsInfo[]> {
    const allPlanContentsInfo: PlanContentsInfo[] = [];

    // firestoreのwhere in の制限により、FIRESTORE_WHERE_IN_LIMIT件ずつ取得する
    for (let i = 0; i < contentsIds.length; i += FIRESTORE_WHERE_IN_LIMIT) {
      const chunk = contentsIds.slice(i, i + FIRESTORE_WHERE_IN_LIMIT);
      const result = await this.getPlanContentsListByIds(planId, chunk);
      allPlanContentsInfo.push(...result);
    }

    return allPlanContentsInfo;
  }

  /**
   * フィルタを作成
   * @param onlyRestricted 入会後経過日数制限ありのコンテンツのみ取得するか
   * @param onlyPublic 公開済みコンテンツのみ取得するか
   * @param updatedAtThreshold 更新日時の閾値
   * @param updatedOpStr 更新日時の比較演算子
   * @returns フィルタ
   */
  private createFilter(
    onlyRestricted: boolean,
    onlyPublic: boolean,
    updatedAtThreshold?: Date,
    updatedOpStr?: WhereFilterOp,
  ): QueryFilterConstraint[] {
    const filters: QueryFilterConstraint[] = [];
    if (onlyPublic) {
      // 公開済みコンテンツのみ取得（一般ユーザー向け）
      filters.push(where("is_public", "==", true));
      if (onlyRestricted) {
        // 入会後経過日数制限ありのコンテンツのみ取得: viewing_restriction_days > 0 でフィルタ
        filters.push(where("viewing_restriction_days", ">", 0));
      } else {
        // 入会後経過日数制限なしのコンテンツのみ取得をしたかったが、
        // ※ Firestoreのorクエリでundefinedは使えないため、フィルタなしで取得し、アプリ側でフィルタ
        // 理由: viewing_restriction_days == 0 または未設定(undefined)のコンテンツを取得したいが
        //       Firestoreでは「フィールドが存在しない」という条件を直接クエリできない
        // filters.push(or(where("viewing_restriction_days", "==", 0), where("viewing_restriction_days", "==", undefined)));
      }
    }
    if (updatedAtThreshold && updatedOpStr) {
      // 更新日時での範囲指定（newerThan/olderThanに対応）
      filters.push(where("updated_at", updatedOpStr, updatedAtThreshold));
    }
    return filters;
  }

  /**
   * 非フィルタを作成
   * @param orderByDirection ソート順
   * @returns 非フィルタ
   */
  private createNonFilter(orderByDirection: OrderByDirection = "asc"): QueryNonFilterConstraint[] {
    return [orderBy("updated_at", orderByDirection)];
  }

  /**
   * コンテンツの未読情報をチェック
   * @param planId プランID
   * @param contentsType コンテンツタイプ
   * @param onlyPublic 公開済みコンテンツのみ取得するか
   * @param userId ユーザーID
   * @param userPlanPriceId ユーザーのプランPriceID（プラン制限チェックに使用）
   * @param userContractDays ユーザーの契約日数（閲覧制限日数チェックに使用）
   * @param onlyRestricted 入会後経過日数制限ありのコンテンツのみ取得するか
   * @param searchDirection ソート順
   * @param updatedAtThreshold 更新日時の閾値
   * @returns 未読情報
   */
  private async checkContents(
    planId: string,
    contentsType: string,
    onlyPublic: boolean,
    userId: string,
    userPlanPriceId?: number,
    userContractDays?: number,
    onlyRestricted: boolean = false,
    searchDirection: OrderByDirection = "desc",
    updatedAtThreshold?: Date,
  ): Promise<CheckUnreadResult> {
    let filters: QueryFilterConstraint[] = this.createFilter(
      onlyRestricted,
      onlyPublic,
      updatedAtThreshold,
      searchDirection === "desc" ? "<=" : ">=",
    );
    const nonFilters: QueryNonFilterConstraint[] = this.createNonFilter(searchDirection);

    let checkedUntilAt: Date | undefined;
    let lastContentDate: Date | undefined;

    // for内でのorderByDirection
    const forOrderByDirection: WhereFilterOp = searchDirection === "desc" ? "<" : ">";

    // 未読が見つかるか、すべてのコンテンツをチェックするまで繰り返す。一旦10万件までとする
    const WHILE_LOOP_MAX_COUNT = 100000 / FIRESTORE_WHERE_IN_LIMIT;
    for (let i = 0; i < WHILE_LOOP_MAX_COUNT; i++) {
      // FIRESTORE_WHERE_IN_LIMITを考慮してlimitを設定
      const limitedNonFilters = [...nonFilters, limit(FIRESTORE_WHERE_IN_LIMIT)];
      const contentsList = await this.getPlanContentsList(planId, contentsType, filters, limitedNonFilters);

      if (contentsList.length === 0) {
        if (searchDirection === "asc") {
          // 新しい方を調べる際には、既読が一つもない場合にはlastUnreadBoundaryをundefinedに設定
          return { result: false, latestCheckInfo: { checkedUntilAt, lastUnreadBoundaryAt: undefined } };
        }
        // 既読が一つもない場合にはlastUnreadBoundaryを最古のコンテンツの日時に設定
        return { result: false, latestCheckInfo: { checkedUntilAt, lastUnreadBoundaryAt: lastContentDate } };
      }

      // コンテンツリストのIDリストからコンテンツ情報を取得
      const contentsIdsSlice = contentsList.map(c => c.contentsId.toString());
      const userContentsList = await this.getUserContentsList(userId, contentsIdsSlice);

      // Mapを使用して検索性能を向上
      const userContentsMap = new Map(userContentsList.map(uc => [uc.id.toString(), uc]));

      for (let j = 0; j < contentsList.length; j++) {
        const content = contentsList[j];

        if (onlyPublic) {
          // 一般ユーザー（onlyPublic=true）の場合は、閲覧可能かチェック。オーナーであればチェックしない
          // プラン制限と閲覧制限日数の両方をチェック（onlyRestrictedに関わらず実施）
          // limitedPlanPriceIdsとviewingRestrictionDaysの両方を考慮して閲覧可能か判定
          const contentIsViewable = isContentsViewable(content, userPlanPriceId, userContractDays);
          if (!contentIsViewable) {
            continue;
          }
        }

        if (!checkedUntilAt || (content.updatedAt && content.updatedAt > checkedUntilAt)) {
          // 最新のチェック日時を更新
          checkedUntilAt = content.updatedAt ?? undefined;
        }

        const userContent = userContentsMap.get(content.contentsId.toString());
        if (!userContent || !userContent.read) {
          // 未読がある
          return {
            result: true,
            latestCheckInfo: {
              checkedUntilAt,
              lastUnreadBoundaryAt: content.updatedAt ?? undefined,
            },
          };
        }
      }

      // 最後のコンテンツの日時を次のクエリの開始点として使用
      const lastContent = contentsList[contentsList.length - 1];
      lastContentDate = lastContent.updatedAt ?? undefined;

      filters = this.createFilter(onlyRestricted, onlyPublic, lastContentDate, forOrderByDirection);
    }

    // 10万件を超えた場合はfalseを返す
    sentryErrorLog(`checkContents: 10万件を超えています: ${planId} ${contentsType} ${onlyPublic} ${userId}`);
    return { result: false, latestCheckInfo: { checkedUntilAt: undefined, lastUnreadBoundaryAt: undefined } };
  }

  /**
   * コンテンツタイプごとの未読情報を取得
   *
   * updated_atベースで未読コンテンツをチェックする。
   * プラン制限（limitedPlanPriceIds）と閲覧制限日数（viewingRestrictionDays）を考慮する。
   *
   * @param planId プランID
   * @param contentsType コンテンツタイプ（movie/article等）
   * @param onlyPublic 公開済みコンテンツのみチェック（一般ユーザー向け）
   * @param userId ユーザーID
   * @param userPlanPriceId ユーザーのプランPriceID（プラン制限チェックに使用）
   * @param userContractDays ユーザーの契約日数（閲覧制限日数チェックに使用）
   * @param options オプション
   * @param options.newerThan この日時より新しいコンテンツをチェック（差分チェック用）
   * @param options.olderThan この日時より古いコンテンツをチェック（遡りチェック用）
   * @param options.onlyRestricted true: 入会後経過日数制限ありのコンテンツのみ, false: 制限なしのみ
   * @returns 未読の有無とチェック情報
   */
  async checkUnread(
    planId: string,
    contentsType: string,
    onlyPublic: boolean,
    userId: string,
    userPlanPriceId?: number,
    userContractDays?: number,
    options?: {
      newerThan?: Date;
      olderThan?: Date;
      onlyRestricted?: boolean; // true: 入会後経過日数制限ありのコンテンツのみ, false: 全てのコンテンツ
    },
  ): Promise<CheckUnreadResult> {
    if (options?.newerThan) {
      // 新しいデータをチェック
      return await this.checkContents(
        planId,
        contentsType,
        onlyPublic,
        userId,
        userPlanPriceId,
        userContractDays,
        options?.onlyRestricted,
        "asc",
        options?.newerThan,
      );
    }
    if (options?.olderThan) {
      // 古いデータをチェック
      return await this.checkContents(
        planId,
        contentsType,
        onlyPublic,
        userId,
        userPlanPriceId,
        userContractDays,
        options?.onlyRestricted,
        "desc",
        options?.olderThan,
      );
    }
    // 全てのデータをチェック
    return await this.checkContents(
      planId,
      contentsType,
      onlyPublic,
      userId,
      userPlanPriceId,
      userContractDays,
      options?.onlyRestricted,
      "desc",
      undefined,
    );
  }

  /**
   * Cloud Function経由でプランの全コンテンツタイプの未読情報を取得
   * @param planId プランID
   * @param userId ユーザーID
   * @param onlyPublic 公開済みコンテンツのみ取得するか
   * @param userPlanPriceId ユーザーのプランPriceID
   * @param userContractDays ユーザーの契約日数
   * @returns 未読リスト
   */
  async checkPlanContentsUnreadAll(
    planId: string,
    userId: string,
    onlyPublic: boolean,
    userPlanPriceId?: number,
    userContractDays?: number,
  ): Promise<{
    unreadList: { [contentsType: string]: boolean };
  }> {
    // APIサーバーのJWTトークンを取得
    const loginStore = useLoginStore();
    const token = loginStore.getSessionToken();

    if (!token) {
      throw new Error("認証トークンが取得できませんでした");
    }

    // Cloud Functionのエンドポイント
    const config = useRuntimeConfig();
    const functionUrl = `${config.public.functionsApiBaseUrl}/checkPlanContentsUnreadAll`;

    // HTTPリクエストで呼び出し
    // latestContentsListはFunctions側でFirestoreから直接取得する
    const response = await axios.post(
      functionUrl,
      {
        planId,
        userId,
        onlyPublic,
        userPlanPriceId,
        userContractDays,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    );

    const data = response.data as {
      unreadList: { [contentsType: string]: boolean };
    };

    return {
      unreadList: data.unreadList,
    };
  }
}
